import React, { Suspense, lazy, useEffect } from 'react';
import { Route, Switch, Redirect } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import queryString from 'query-string';
import { useLocation } from 'react-router';
import { useRecoilState } from 'recoil';
import PrivateRoute from './PrivateRoute';
import Loader from './components/common/Loader';
import { checkFirstOpen, setNavTab } from './modules/status';
import useKb from './hooks/Kb/useKb';
import useFcmToken from './hooks/FcmToken/useFcmToken';
import useOnResultAction from 'hooks/Native/useOnResultAction';
import useOnErrorAction from 'hooks/Native/useOnErrorAction';

import BottomSheet from 'components/common/BottomSheet/BottomSheet';
import Login from 'components/account/login/Login';
import {
  setActiveLoginBottomSheet,
  getIsActiveLoginBottomSheet,
} from 'modules/account';
import { useAppSelector } from './modules';
import { userLanguage } from 'i18n';
import {
  useCheckValidAppVersion,
  preScalpV3AndForeheadV2,
} from 'hooks/Native/useCheckAppVersionValid';

import { currentCenterModalState } from 'recoil/@common/centerModalState';
import CenterModal from 'components/CenterModal/CenterModal';
//--------------------------------------------------------------------  PAGES START ------------------------------------------------------------
import Header from './components/common/Header';
import NavBar from 'components/common/NavBar';
import DrawerPage from './pages/drawer/DrawerPage';
import useAutoLogin from 'hooks/AutoLogin/useAutoLogin';
import useUserSelectCouponAndPoint from 'hooks/UserSelectCouponAndPoint/useUserSelectCouponAndPoint';
import { getMainCurrentTab } from './modules/status';
import { getTeleMedicineServiceStatus } from 'modules/telemedicine';
import { useChangeLanguage } from 'hooks/useChangeLanguage';
import AppVersionNotice from 'components/AppVersionNotice/AppVersionNotice';

const MainPage = lazy(() => import('./pages/main/MainPage'));
const QuizPage = lazy(() => import('./pages/quiz/QuizPage'));
const AnalysisPage = lazy(() => import('./pages/analysis/analysisPage'));
const ScalpResultV2Page = lazy(() =>
  import('./pages/scalpResultV2/scalpResultV2Page'),
);
const KitDetailPage = lazy(() => import('./pages/KitDetail'));
const ProductDetailPage = lazy(() => import('./pages/productDetail'));
const ShampooPage = lazy(() => import('./pages/shampoo/ShampooPage'));
const ForeheadPage = lazy(() => import('./pages/forehead/ForeheadPage'));
const LoginPage = lazy(() => import('./pages/account/LoginPage'));
const RegisterPage = lazy(() => import('./pages/account/RegisterPage'));
const CountryChoicePage = lazy(() => import('./pages/account/CountryChoice'));
const UserAddPage = lazy(() => import('./pages/user/UserAddPage'));
const ForgotPage = lazy(() => import('./pages/account/ForgotPage'));
const PasswordChangePage = lazy(() =>
  import('./pages/account/PasswordChangePage'),
);
const CartPage = lazy(() => import('./pages/cart'));
const PaymentPage = lazy(() => import('./pages/payment'));
const PaymentResultPage = lazy(() => import('./pages/paymentResult'));

const DevicePromotionPage = lazy(() =>
  import('./pages/promotion/DevicePromotionPage'),
);
const TelemedicinePage = lazy(() => import('./pages/Telemedicine'));
//--------------------------------------------------------------------  PAGES END ------------------------------------------------------------
//--------------------------------------------------------------------  IN DRAWER START ------------------------------------------------------------
const VersionPage = lazy(() => import('./pages/drawer/VersionPage'));
const TermsPage = lazy(() => import('./pages/drawer/TermsPage'));
const OpensourcePage = lazy(() => import('./pages/drawer/OpensourcePage'));
//--------------------------------------------------------------------  IN DRAWER END ------------------------------------------------------------
// DEVICE TEST
const ScalpPage = lazy(() => import('./pages/scalp/ScalpPage'));
const HairlossProgressPage = lazy(() =>
  import('./pages/hairlossProgress/HairlossProgressPage'),
);

const App = () => {
  const dispatch = useDispatch();
  // =================== state =================== //
  const isValidVersion = useCheckValidAppVersion(
    preScalpV3AndForeheadV2.android,
    preScalpV3AndForeheadV2.ios,
  );
  const [isOpenCenterModal, setIsOpenCenterModal] = useRecoilState(
    currentCenterModalState,
  );
  useEffect(() => {
    if (isValidVersion) {
      setIsOpenCenterModal(false);
    } else {
      setIsOpenCenterModal(true);
    }
  }, [isValidVersion, setIsOpenCenterModal]);

  const { isAuthenticated, users, firstOpen, navTab, fcmToken, fromModaol } =
    useSelector(({ account, user, status }) => ({
      isAuthenticated: account.account?.account_id ? true : false,
      users: user.users,
      language: status.language,
      3: status.firstOpen,
      navTab: status.navTab,
      fcmToken: status.fcmToken,
    }));
  const MainNavTap = useAppSelector(getMainCurrentTab);
  const isTeleMedicineService = useAppSelector(getTeleMedicineServiceStatus);
  const isActiveLoginBottomSheet = useAppSelector(getIsActiveLoginBottomSheet);
  const location = useLocation();
  // NOTE : mnt -> main nav tab
  const { refresh_token, from, mnt } = queryString.parse(location.search);

  // =================== hook =================== //
  useChangeLanguage(userLanguage);
  // useChangeLanguage('ja');
  useKb();
  useFcmToken(fcmToken, users);
  useAutoLogin(isAuthenticated, users, refresh_token, from);
  useUserSelectCouponAndPoint(users, from);
  useEffect(() => {
    if (mnt) {
      dispatch(setNavTab(Number(mnt)));
    }
  }, [mnt, dispatch]);

  // =================== 네이티브(AOS, IOS) -> 웹뷰 통신 정의  =================== //
  useOnResultAction();
  useOnErrorAction();

  // =================== ETC  =================== //
  const onCloseLoginBottomSheet = () => {
    dispatch(setActiveLoginBottomSheet(false));
  };

  return (
    <Suspense fallback={<Loader />}>
      {isOpenCenterModal && (
        <CenterModal>
          <AppVersionNotice />
        </CenterModal>
      )}
      <div
        className="app"
        style={{
          backgroundColor:
            isTeleMedicineService ||
            (window.location.pathname === '/' && MainNavTap !== 0)
              ? '#ffff'
              : 'rgb(238, 237, 239)',
          minHeight: '100vh',
        }}
      >
        <Header isTeleMedicineService={isTeleMedicineService} />
        <Switch>
          {/* ============ MAIN ============ */}
          <Route render={() => <MainPage />} path="/" exact />
          {/* ============ QUIZ ============ */}
          <Route component={QuizPage} path="/quiz/:id" exact />
          {/* ============ SCALP_RESULT_V2 ============ */}
          <Route component={ScalpResultV2Page} path="/scalp/result/v2" exact />
          {/* ============ ANALYSIS ============ */}
          <Route component={AnalysisPage} path="/analysis" exact />
          {/* ============ PRODUCT_DETAIL ============ */}
          <Route component={ProductDetailPage} path="/product/detail" exact />
          {/* ============ KIT_DETAIL ============ */}
          <Route component={KitDetailPage} path="/kit/detail" exact />
          {/* ============ CART ============ */}
          <Route component={CartPage} path="/product/cart" exact />
          {/* ============ Payment ============ */}
          <Route component={PaymentPage} path="/payment/normal" exact />
          {/* ============ PaymentResult ============ */}
          <Route
            component={PaymentResultPage}
            path="/payment/:type/result"
            exact
          />

          {/* ============ SHAMPOO ============ */}
          <Route
            component={ShampooPage}
            path={[
              '/shampoo/search',
              '/shampoo/register/:type',
              '/shampoo/condition',
              '/shampoo/condition/optional',
              '/shampoo/result',
              '/shampoo/ingredients',
              '/shampoo/rule',
            ]}
          />
          {/* FOREHEAD */}
          <PrivateRoute
            component={ForeheadPage}
            path={['/forehead', '/forehead/result', '/forehead/simulation']}
          />
          {/* ============ ACCOUNT & USER ============ */}
          <Route component={LoginPage} path="/login" />
          <Route component={CountryChoicePage} path="/register/country" />
          <Route component={RegisterPage} path="/register/pw" />
          <Route
            component={ForgotPage}
            path={['/forgot/email', '/forgot/code', '/forgot/passwordreset']}
          />
          <PrivateRoute
            component={PasswordChangePage}
            path={'/account/update/password'}
          />
          <Route
            component={UserAddPage}
            path={[
              '/adduser/tel',
              '/adduser/terms',
              '/adduser/profile',
              '/adduser/finish',
            ]}
          />
          {/* ============ DRAWER ============ */}
          <PrivateRoute
            component={DrawerPage}
            path={['/drawer', '/drawer/coupon']}
          />
          <Route component={VersionPage} path={['/version']} />
          <Route
            component={TermsPage}
            path={[
              '/terms',
              '/terms/service',
              '/terms/privacy',
              '/terms/sensitivity',
            ]}
          />
          <Route component={OpensourcePage} path={['/opensource']} />
          {/* ============ DEVICE TEST ============ */}
          <Route
            component={ScalpPage}
            path={[
              '/scalp/guide',
              '/scalp/menu',
              '/scalp/historys',
              '/scalp/result',
              '/scalp/dummy/result',
              // '/scalp/hairloss/result',
              '/scalp/compare/result',
            ]}
          />
          {/* ============ 웹뷰 건강두피 vs 탈모두피 비교 분석 page redirect. ============ */}
          <Route
            component={HairlossProgressPage}
            path={['/hairloss-progress/result']}
          />
          {/* # 프로모션 - 2022.07 현재 디바이스 사전예약 진행중  */}
          <Route component={DevicePromotionPage} path={'/promotion/device'} />
          {/* ============ 원격진료 ============ */}
          <Route
            render={() => <TelemedicinePage />}
            path={['/telemedicine', '/telemedicine/medicalreception']}
          />
          {/* ============ 딥링크(원링크) 랜딩 페이지, deep_link_value에 해당하는 경로로 바로 redirect해줌  ============ */}
          <Route
            path="/landing/:deep_link_value"
            render={(routeProps) => (
              <Redirect
                to={`${routeProps.location.pathname.split('/landing')[1]}`}
              />
            )}
          />
        </Switch>
        <NavBar
          firstOpen={firstOpen}
          navTab={navTab}
          setNavTab={(newNavTab) => dispatch(setNavTab(newNavTab))}
          checkFirstOpen={() => dispatch(checkFirstOpen(false))}
        />
        <div
          className="safeAreaTop"
          style={
            isTeleMedicineService ||
            (window.location.pathname === '/' && MainNavTap !== 0)
              ? { backgroundColor: '#ffff' }
              : { backgroundColor: 'rgb(238, 237, 239)' }
          }
        ></div>
        {/* login bottom sheet */}
        <BottomSheet
          bottomSheetOpen={isActiveLoginBottomSheet}
          onCloseBottomSheet={onCloseLoginBottomSheet}
        >
          <Login onCloseLoginBottomSheet={onCloseLoginBottomSheet} />
        </BottomSheet>
      </div>
    </Suspense>
  );
};
export default App;
